/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { NavLink } from 'react-router';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import SensorRequestMessage from '@/components/feedback/SensorRequestMessage';
import { ElectricityNightIcon } from '@/components/icon/SupplyIcon';
import DashboardPanelHeader from '@/components/layout/DashboardPanelHeader';

/* Import configuration */
import type { OverviewAnomalyConfig } from '@/config/kpi/overviewAnomalyConfig';

/* Import libraries */
import { NamingConventions } from '@/lib/formatting/namingConventions';
import {
    getOverviewKpiSurfaceClassName,
    getOverviewStatusClass,
    type OverviewCardSortData,
} from '@/lib/overview/overviewCards';

/* Import types */
import type { Api2ValueDataItem } from '@/lib/overview/api2ValueData';
import type { SensorMetadataEntry } from '@/lib/sensors/csv/csvToSensorMetadata';

export interface OverviewSensorCardData extends OverviewCardSortData {
    address: string | null;
    anomalyConfig: OverviewAnomalyConfig;
    deviceName: string;
    deviceSensorPart: string;
    errorMessage: string;
    isEnabled: boolean;
    item: Api2ValueDataItem;
    metadata: SensorMetadataEntry;
    name: string;
    status: number;
}

interface OverviewSensorCardProps {
    card: OverviewSensorCardData;
    showKpis: boolean;
}

function OverviewSensorCard({ card, showKpis }: OverviewSensorCardProps) {
    const { t } = useI18n();
    const {
        anomalyConfig,
        deviceName,
        deviceSensorPart,
        errorMessage,
        isEnabled,
        item,
        metadata,
        status,
    } = card;
    const Icon = metadata.anomaly === 'bl_night'
        ? ElectricityNightIcon
        : anomalyConfig.icon;
    const anomalyTooltip = metadata.anomaly
        ? t(`anomaly_${metadata.anomaly}`)
        : anomalyConfig.badgeLabel;

    if (errorMessage && isEnabled) {
        return (
            <div className="dashboard-panel interactive-surface !min-h-52 h-full items-start justify-start !overflow-visible p-0 text-left no-underline transition-[background,transform] duration-150">
                <div className="flex h-full w-full flex-col">
                    <DashboardPanelHeader
                        actions={(
                            <>
                                <span className="badge badge-error badge-soft">
                                    {t('Error')}
                                </span>
                                {metadata.system ? (
                                    <span className="badge border-base-content/15 bg-base-content/10 text-base-content">
                                        {metadata.system}
                                    </span>
                                ) : null}
                            </>
                        )}
                        icon={Icon}
                        iconTooltip={anomalyTooltip}
                        title={deviceSensorPart}
                    />
                    <div className="flex flex-1 items-center px-5 py-4">
                        <div className="mt-4 w-full rounded-box border border-error/30 bg-error/10 px-3 py-2 text-sm leading-relaxed text-error">
                            <SensorRequestMessage message={errorMessage} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const content = (
            <div className="flex h-full w-full flex-col">
                <DashboardPanelHeader
                    actions={(
                        <>
                            {!isEnabled ? (
                                <span className="badge badge-ghost">
                                    {t('Disabled sensor')}
                                </span>
                            ) : null}
                            {metadata.system ? (
                                <span className="badge border-base-content/15 bg-base-content/10 text-base-content">
                                    {metadata.system}
                                </span>
                            ) : null}
                        </>
                    )}
                    icon={Icon}
                    iconTooltip={anomalyTooltip}
                    title={deviceSensorPart}
                />

                <div className="flex flex-1 flex-col px-5 py-3">
                    <div className="flex flex-col justify-start gap-2">
                        {metadata.description ? (
                            <p className="[display:-webkit-box] overflow-hidden mb-0 text-sm leading-relaxed text-base-content/80 [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                                <span className="font-medium text-base-content/80">{t('Description')}:</span>{' '}
                                {metadata.description}
                            </p>
                        ) : null}

                        {metadata.medium ? (
                            <p className="[display:-webkit-box] overflow-hidden mb-0 text-sm leading-relaxed text-base-content/65 [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                                <span className="font-medium text-base-content/80">{t('Medium')}:</span>{' '}
                                {metadata.medium}
                            </p>
                        ) : null}
                    </div>

                    {showKpis ? (
                        <div className="mt-4">
                            <div className="flex items-center justify-between gap-3 rounded-box bg-base-100/70 px-3 py-2">
                                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                    {t('Status')}
                                </span>
                                <span className="text-sm font-semibold text-base-content">
                                    {isEnabled && anomalyConfig.statusLabels[status]
                                        ? t(anomalyConfig.statusLabels[status])
                                        : isEnabled
                                            ? '-'
                                            : t('Disabled sensor')}
                                </span>
                            </div>
                        </div>
                    ) : null}

                    {showKpis ? (
                        <div className={`mt-2 grid gap-2 ${anomalyConfig.kpiGridClassName}`}>
                            {anomalyConfig.kpis.map((kpi) => {
                                const formattedValue = item.formattedValue.get(kpi.sensorType);
                                const unit = item.unit.get(kpi.sensorType);

                                return (
                                    <div
                                        key={kpi.sensorType}
                                        className={`rounded-box px-3 py-2 ${isEnabled ? getOverviewKpiSurfaceClassName(metadata.anomaly) : 'bg-base-100/50'}`}
                                        title={`${t(anomalyConfig.label)} (${anomalyConfig.badgeLabel})`}
                                    >
                                        <p className="mb-0 text-[0.7rem] font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                            {new NamingConventions(kpi.sensorType).getTitle()}
                                        </p>
                                        <p className="mb-0 mt-1 truncate text-sm font-semibold tabular-nums text-base-content">
                                            {formattedValue !== null
                                                ? `${formattedValue}${unit ? ` ${unit}` : ''}`
                                                : '-'}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
    );

    if (!isEnabled) {
        return (
            <article
                className="dashboard-panel h-full items-start justify-start !overflow-visible bg-base-200/70 p-0 text-left text-base-content/60 opacity-70"
                aria-label={`${t(anomalyConfig.label)} ${deviceName}`}
            >
                {content}
            </article>
        );
    }

    return (
        <NavLink
            to={`${anomalyConfig.route}/${deviceName}`}
            className={`dashboard-panel interactive-surface h-full items-start justify-start !overflow-visible p-0 text-left no-underline transition-[background,transform] duration-150 ${getOverviewStatusClass(status)}`}
            aria-label={`${t(anomalyConfig.label)} ${deviceName}`}
        >
            {content}
        </NavLink>
    );
}

export default OverviewSensorCard;
